import { ActionTypes } from '../../../Common/Constants';
import { ErrorTypes } from '../../../Common/Constants/ErrorTypes.Constants';
import TelemetryService from '../../../Services/Services.Primary/Telemetry/TelemetryService';

export type PreFetchPromise = {
  timestamp: number;
  promise: Promise<any>;
};

export class PreFetchService {
  private static instance: PreFetchService;
  private preFetchMapping = new Map<string, PreFetchPromise>();
  private telemetryService = TelemetryService.getInstance();
  private preFetchLimit = 4;
  private timeIgnore = 1800000;

  public static getInstance() {
    if (!PreFetchService.instance) {
      PreFetchService.instance = new PreFetchService();
    }
    return PreFetchService.instance;
  }

  public preFetchData<T>(uniqueId: string, sourceFn: (...parameters: any[]) => Promise<T>, ...parameters: any[]): void {
    // Ensures that we don't exceed the limit of preFetch calls.
    if (this.preFetchMapping.size === this.preFetchLimit) {
      return;
    }

    // If the promise is old it is better to request a new one.
    if (this.isPreFetchedPromiseFresh(uniqueId)) {
      return;
    }

    const preFetchPromise = new Promise((resolve: (value: T | null) => void, reject: (reason?: any) => void) => {
      try {
        const response = sourceFn(...parameters);
        response
          .then((value: T) => {
            resolve(value);
          })
          .catch(error => {
            reject(error);
          });
      } catch (error) {
        const errorLog = {
          name: ErrorTypes.prefetchFailed,
          message: 'Pre Fetch call Failed',
          uniqueId: uniqueId,
        };

        this.telemetryService.traceError(ErrorTypes.prefetchFailed, errorLog);
        reject(error);
      }
    });

    this.preFetchMapping.set(uniqueId, {
      timestamp: new Date().getTime(),
      promise: preFetchPromise,
    });
  }

  public getPreFetchedPromise(uniqueId: string): Promise<any> | undefined {
    if (this.checkPreFetchedId(uniqueId) && this.isPreFetchedPromiseFresh(uniqueId)) {
      const preFetchPromise = this.preFetchMapping.get(uniqueId);

      if (preFetchPromise) {
        const eventLog = {
          name: ActionTypes.preFetchDataUtilized,
          preFetchStarted: preFetchPromise?.timestamp as number,
          timeRequested: new Date().getTime(),
          uniqueId: uniqueId,
          timeSaved: 0,
        };
        eventLog.timeSaved = eventLog.timeRequested - eventLog.preFetchStarted;
        this.telemetryService.traceEvent(ActionTypes.preFetchDataUtilized, eventLog);

        // Delete the entry so that it allows for fresh data to be fetched.

        this.preFetchMapping.delete(uniqueId);
        return preFetchPromise?.promise;
      }
    }

    return undefined;
  }

  private checkPreFetchedId(uniqueId: string): boolean {
    if (this.preFetchMapping.has(uniqueId)) {
      return true;
    }

    return false;
  }

  // The responsibility of this is to check if the prefetched promise is too old to be used.
  private isPreFetchedPromiseFresh(uniqueId: string): boolean | undefined {
    if (this.checkPreFetchedId(uniqueId)) {
      const preFetchPromise = this.preFetchMapping.get(uniqueId);
      if (preFetchPromise) {
        const timeDiff = new Date().getTime() - preFetchPromise.timestamp;
        if (timeDiff > this.timeIgnore) {
          return false;
        }

        return true;
      }
    }
    return undefined;
  }
}
